/**
 * Formatting utilities for consistent decimal precision across the application
 */

import { 
  DEFAULT_CURRENCY_PRECISION, 
  DEFAULT_EXTENDED_CURRENCY_PRECISION,
  DEFAULT_WEIGHT_PRECISION,
  DEFAULT_PURITY_PRECISION,
  DEFAULT_PER_METAL_PRECISION
} from '@/util/refinery/constants'

/**
 * Semantic precision map for commonly-used formatting contexts
 * Maps semantic strings to their corresponding decimal precision constants
 */
const SEMANTIC_PRECISION = {
  weight: DEFAULT_WEIGHT_PRECISION,
  purity: DEFAULT_PURITY_PRECISION,
  currency: DEFAULT_CURRENCY_PRECISION,
  'currency-precise': DEFAULT_EXTENDED_CURRENCY_PRECISION,
  percent: 2
}

/**
 * Resolve a precision value to a numeric decimal count
 * Supports semantic strings ('weight', 'currency', etc.) and explicit numbers
 * Falls back to weight precision for unknown values
 * 
 * @param {string|number|null|undefined} value - Precision specifier
 * @returns {number} Resolved decimal places
 * 
 * @example
 * resolvePrecision('weight')    // 4
 * resolvePrecision('currency')  // 2
 * resolvePrecision(3)           // 3
 * resolvePrecision('unknown')   // 4 (falls back to weight precision)
 */
export function resolvePrecision(value) {
  if (typeof value === 'number') {
    return value
  }
  
  if (typeof value === 'string' && SEMANTIC_PRECISION[value]) {
    return SEMANTIC_PRECISION[value]
  }
  
  return DEFAULT_WEIGHT_PRECISION
}

/**
 * Format a numeric value to specified decimal precision
 * This is the foundational primitive for all decimal formatting in the app
 * Supports both semantic strings ('weight', 'currency') and explicit numbers
 * 
 * @param {number|string|null|undefined} value - Value to format
 * @param {number|string|undefined} decimals - Number of decimal places or semantic string
 * @param {string} [fallback] - Optional fallback for null/invalid values (default: zero with appropriate decimals)
 * @returns {string} Formatted number string or fallback for invalid values
 * 
 * @example
 * formatToPrecision(10.5, 4)           // '10.5000'
 * formatToPrecision(10.5)              // '10.5000' (uses default weight precision)
 * formatToPrecision(10.5, 'weight')    // '10.5000'
 * formatToPrecision(92.87, 'currency') // '92.87'
 * formatToPrecision(null, 4)           // '0.0000' (default zero fallback)
 * formatToPrecision(null, 4, '--')     // '--' (explicit fallback)
 * formatToPrecision(null, 'weight', 'N/A') // 'N/A' (custom fallback)
 */
export function formatToPrecision(value, decimals, fallback) {
  // Parse to number
  const num = parseFloat(value)

  // Handle non-numeric values (null, undefined, NaN, Infinity, -Infinity)
  if (!isFinite(num)) {
    // If no explicit fallback provided, generate zero with appropriate decimals
    if (fallback === undefined) {
      const decimalPlaces = resolvePrecision(decimals)
      return (0).toFixed(decimalPlaces)
    }
    return fallback
  }

  // Resolve semantic strings or explicit numbers to decimal places
  const decimalPlaces = resolvePrecision(decimals)

  // Format to specified precision
  return num.toFixed(decimalPlaces)
}

/**
 * Format a numeric value as USD currency with standard precision (2 decimal places)
 * 
 * @param {number|string|null|undefined} value - Value to format as currency
 * @returns {string} Formatted currency string (e.g., '$1,234.56') or empty string for invalid values
 * 
 * @example
 * formatToCurrency(1234.5678) // '$1,234.57'
 * formatToCurrency(null) // ''
 */
export function formatToCurrency(value) {
  if (typeof value !== 'number') {
    value = parseFloat(value)
  }
  
  if (!isFinite(value)) {
    return ''
  }
  
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: DEFAULT_CURRENCY_PRECISION,
    maximumFractionDigits: DEFAULT_CURRENCY_PRECISION,
  })
  
  return formatter.format(value)
}

/**
 * Format a numeric value as USD currency with extended precision (4 decimal places)
 * Used for spot prices and precise transaction values
 * 
 * @param {number|string|null|undefined} value - Value to format as currency
 * @returns {string} Formatted currency string (e.g., '$1,234.5678') or empty string for invalid values
 * 
 * @example
 * formatToCurrencyPrecise(1234.5678) // '$1,234.5678'
 * formatToCurrencyPrecise(null) // ''
 */
export function formatToCurrencyPrecise(value) {
  if (typeof value !== 'number') {
    value = parseFloat(value)
  }
  
  if (!isFinite(value)) {
    return ''
  }
  
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: DEFAULT_EXTENDED_CURRENCY_PRECISION,
    maximumFractionDigits: DEFAULT_EXTENDED_CURRENCY_PRECISION,
  })
  
  return formatter.format(value)
}

/**
 * Format a numeric string/number with thousands separators while preserving fixed decimals
 * Useful for weight displays like ozt where we want grouping but fixed precision
 *
 * @param {number|string|null|undefined} value - Value to format
 * @param {number|string} decimals - Decimal places or semantic string (defaults to 'weight')
 * @returns {string} Formatted number string with grouping (e.g., '1,234.5678')
 */
export function formatWithGrouping(value, decimals = 'weight') {
  const fixed = formatToPrecision(value, decimals, '--')
  if (fixed === '--') return fixed

  // Preserve sign and decimals, group integer part
  const isNegative = fixed.startsWith('-')
  const normalized = isNegative ? fixed.slice(1) : fixed
  const [intPart, decPart] = normalized.split('.')
  const groupedInt = Number(intPart).toLocaleString('en-US')
  return `${isNegative ? '-' : ''}${decPart !== undefined ? `${groupedInt}.${decPart}` : groupedInt}`
}

/**
 * Format a numeric weight value as troy ounces with standard precision
 * Used for displaying metal weights in refinery workflows
 * 
 * @param {number|string|null|undefined} value - Weight value to format
 * @returns {string} Formatted weight string with ozt suffix (e.g., '12.3456 ozt') or '0.0000 ozt' for invalid values
 * 
 * @example
 * formatToOzt(12.3456) // '12.3456 ozt'
 * formatToOzt(5.1) // '5.1000 ozt'
 * formatToOzt(null) // '0.0000 ozt'
 */
export function formatToOzt(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.0000 ozt'
  }
  return `${formatToPrecision(Number(value), 'weight')} ozt`
}


/**
 * Format to precision based on metal-aware settings.
 * Delegates to formatToPrecision using per-metal decimal settings when provided.
 *
 * @param {number|string|null|undefined} value - Value to format
 * @param {string|null|undefined} metalSymbol - Metal symbol (e.g., 'AU', 'AG', 'PT', 'PD')
 * @param {object|null|undefined} options - Optional options bag
 * @param {object|null|undefined} options.settings - Settings object that may include perMetalWeightPrecision map
 * @returns {string} Formatted number string
 */
export function formatToPrecisionMetal(value, metalSymbol, options = {}) {
  if (!metalSymbol) return formatToPrecision(value, 'weight')

  const key = String(metalSymbol).toUpperCase()
  const settingsPrecision = options?.settings?.perMetalWeightPrecision?.[key]
  const precision = typeof settingsPrecision === 'number' 
    ? settingsPrecision 
    : (DEFAULT_PER_METAL_PRECISION[key] ?? 'weight')

  return formatToPrecision(value, precision)
}

